import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search } from "lucide-react";
import { PageHeader } from "@/components/layout/page-header";
import { Card, CardContent } from "@/components/ui/card";
import { LoadingState, ErrorState } from "@/components/ui/empty";
import { StatusBadge, KindBadge } from "@/components/status-badges";
import { useApi } from "@/lib/api";
import { formatFitness, formatRelative, shortId } from "@/lib/format";
import type {
  ExperimentsResponse,
  ExperimentStatus,
  RungsResponse,
} from "@/lib/types";
import { cn } from "@/lib/utils";

type StatusFilter = ExperimentStatus | "all";

export function ExperimentsPage() {
  const experiments = useApi<ExperimentsResponse>("/api/experiments");
  const rungs = useApi<RungsResponse>("/api/rungs");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [rungFilter, setRungFilter] = useState<string>("all");
  const [search, setSearch] = useState("");

  const rungThresholds = useMemo(() => {
    const m = new Map<string, number>();
    for (const r of rungs.data?.rungs ?? []) m.set(r.id, r.fitness_threshold);
    return m;
  }, [rungs.data]);

  const filtered = useMemo(() => {
    const rows = experiments.data?.experiments ?? [];
    return rows.filter((e) => {
      if (statusFilter !== "all" && e.status !== statusFilter) return false;
      if (rungFilter !== "all" && e.rung_id !== rungFilter) return false;
      if (
        search &&
        !`${e.name} ${e.rung_id} ${e.engine_version} ${e.notes ?? ""}`
          .toLowerCase()
          .includes(search.toLowerCase())
      ) {
        return false;
      }
      return true;
    });
  }, [experiments.data, statusFilter, rungFilter, search]);

  const counts = useMemo(() => {
    const rows = experiments.data?.experiments ?? [];
    return {
      all: rows.length,
      running: rows.filter((e) => e.status === "running").length,
      completed: rows.filter((e) => e.status === "completed").length,
      failed: rows.filter((e) => e.status === "failed").length,
    };
  }, [experiments.data]);

  const uniqueRungs = useMemo(() => {
    const rows = experiments.data?.experiments ?? [];
    return Array.from(new Set(rows.map((e) => e.rung_id))).sort();
  }, [experiments.data]);

  if (experiments.loading) return <LoadingState />;
  if (experiments.error)
    return <ErrorState error={experiments.error} onRetry={experiments.refetch} />;

  return (
    <>
      <PageHeader
        title="Experiments"
        description={`${experiments.data?.count ?? 0} total across evolution, trials, sweeps, and probes.`}
      />

      {/* Filters */}
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <FilterChip
          label="All"
          count={counts.all}
          active={statusFilter === "all"}
          onClick={() => setStatusFilter("all")}
        />
        <FilterChip
          label="Running"
          count={counts.running}
          active={statusFilter === "running"}
          onClick={() => setStatusFilter("running")}
          accent="amber"
        />
        <FilterChip
          label="Completed"
          count={counts.completed}
          active={statusFilter === "completed"}
          onClick={() => setStatusFilter("completed")}
          accent="emerald"
        />
        <FilterChip
          label="Failed"
          count={counts.failed}
          active={statusFilter === "failed"}
          onClick={() => setStatusFilter("failed")}
          accent="rose"
        />

        <div className="ml-auto flex items-center gap-2">
          <select
            value={rungFilter}
            onChange={(e) => setRungFilter(e.target.value)}
            className="h-8 rounded-md border bg-background px-2 text-xs"
          >
            <option value="all">All rungs</option>
            {uniqueRungs.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
          <div className="relative">
            <Search className="absolute left-2 top-1/2 h-3 w-3 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-8 w-48 rounded-md border bg-background pl-7 pr-2 text-xs placeholder:text-muted-foreground/60 focus:outline-none focus:ring-1 focus:ring-ring"
            />
          </div>
        </div>
      </div>

      <Card className="overflow-hidden">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b bg-muted/30 text-left text-[10px] uppercase tracking-wider text-muted-foreground">
                  <th className="px-3 py-2 font-medium">Status</th>
                  <th className="px-3 py-2 font-medium">Kind</th>
                  <th className="px-3 py-2 font-medium">Rung</th>
                  <th className="px-3 py-2 font-medium">Engine</th>
                  <th className="px-3 py-2 font-medium">Fitness</th>
                  <th className="px-3 py-2 font-medium">Pass</th>
                  <th className="px-3 py-2 font-medium">Runner</th>
                  <th className="px-3 py-2 font-medium">Started</th>
                  <th className="px-3 py-2 font-medium">ID</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((e) => {
                  const threshold = rungThresholds.get(e.rung_id);
                  const passed =
                    e.final_fitness !== null &&
                    threshold !== undefined &&
                    e.final_fitness >= threshold;
                  return (
                    <tr
                      key={e.id}
                      className="border-b transition-colors hover:bg-accent"
                    >
                      <td className="px-3 py-2">
                        <StatusBadge status={e.status} />
                      </td>
                      <td className="px-3 py-2">
                        <KindBadge kind={e.kind} />
                      </td>
                      <td className="px-3 py-2">
                        <Link
                          to={`/experiments/${e.id}`}
                          className="font-mono font-medium hover:text-primary"
                        >
                          {e.rung_id}
                        </Link>
                      </td>
                      <td className="px-3 py-2 font-mono text-muted-foreground">
                        {e.engine_version}
                      </td>
                      <td className="px-3 py-2 font-mono tabular-nums">
                        <span
                          className={cn(
                            passed
                              ? "text-emerald-300"
                              : e.final_fitness !== null
                                ? "text-amber-300"
                                : "text-muted-foreground",
                          )}
                        >
                          {formatFitness(e.final_fitness)}
                        </span>
                        {threshold && (
                          <span className="ml-1 text-muted-foreground">
                            / {threshold.toFixed(2)}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        {passed ? (
                          <span className="text-emerald-400">✓</span>
                        ) : e.final_fitness !== null ? (
                          <span className="text-muted-foreground">—</span>
                        ) : (
                          <span className="text-muted-foreground">·</span>
                        )}
                      </td>
                      <td className="px-3 py-2 font-mono text-muted-foreground">
                        {e.runner_id ?? "—"}
                      </td>
                      <td className="px-3 py-2 text-muted-foreground">
                        {formatRelative(e.started_at)}
                      </td>
                      <td className="px-3 py-2 font-mono text-muted-foreground">
                        {shortId(e.id)}
                      </td>
                    </tr>
                  );
                })}
                {filtered.length === 0 && (
                  <tr>
                    <td
                      colSpan={9}
                      className="px-3 py-12 text-center text-muted-foreground"
                    >
                      No experiments match these filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </>
  );
}

function FilterChip({
  label,
  count,
  active,
  onClick,
  accent,
}: {
  label: string;
  count: number;
  active: boolean;
  onClick: () => void;
  accent?: "amber" | "emerald" | "rose";
}) {
  const accentCls = {
    amber: "data-[active=true]:border-amber-500/60 data-[active=true]:bg-amber-500/15 data-[active=true]:text-amber-300",
    emerald:
      "data-[active=true]:border-emerald-500/60 data-[active=true]:bg-emerald-500/15 data-[active=true]:text-emerald-300",
    rose: "data-[active=true]:border-rose-500/60 data-[active=true]:bg-rose-500/15 data-[active=true]:text-rose-300",
  };
  return (
    <button
      type="button"
      data-active={active}
      onClick={onClick}
      className={cn(
        "inline-flex h-8 items-center gap-1.5 rounded-md border px-3 text-xs transition-colors",
        "hover:bg-accent",
        "data-[active=true]:bg-primary data-[active=true]:text-primary-foreground",
        accent && accentCls[accent],
      )}
    >
      {label}
      <span className="text-[10px] opacity-70">{count}</span>
    </button>
  );
}
